/**
 * The Director Studio's pure model: what the fan picks in the Director sheet, how it maps onto
 * the saved profile (`PATCH director-profile`) and onto one director request, which voice states
 * the server can honestly offer, and the word-timing maths behind karaoke captions.
 * Everything here is unit-tested; components only render it.
 */
import {
  DIRECTOR_ENERGIES, DIRECTOR_FOCUSES, DIRECTOR_LENGTHS, DIRECTOR_SPOILERS, DIRECTOR_STYLES, LENGTH_MOMENTS,
  type DirectorEnergy, type DirectorLanguage, type DirectorLength, type DirectorProfile, type DirectorResponse,
  type DirectorShow, type DirectorStyle, type DirectorVoice,
} from '@clutch/core';

export type Familiarity = DirectorProfile['familiarity'];
/** Who the show is about. Teams/players map onto the profile's follows (the server's lens). */
export type Focus = { kind: 'team'; team: string } | { kind: 'player'; name: string } | { kind: 'game' } | { kind: 'analysis' } | { kind: 'calls' };

export type ShowDraft = {
  style: DirectorStyle;
  energy: DirectorEnergy;
  length: DirectorLength;
  voiceId?: string;
  language: DirectorLanguage;
  familiarity: Familiarity;
  focus: Focus;
  /** Catch me up to now (through-cutoff) vs the full finished-game story (complete). */
  spoilers: DirectorShow['spoilers'];
};

export const DEFAULT_SHOW: DirectorShow = { style: 'play_by_play', energy: 'balanced', length: 'standard', focus: 'drama', spoilers: 'through-cutoff' };
const VOICE_ID = /^[A-Za-z0-9_-]{4,64}$/;
const TEAM = /^[A-Z]{3}$/;

const oneOf = <T extends string>(list: readonly T[], v: unknown, d: T): T => (list as readonly unknown[]).includes(v) ? v as T : d;

/** Saved show › local memory › defaults. Unknown values from an older/newer server are ignored. */
export function showFromProfile(profile?: { show?: Partial<DirectorShow> }, local?: Partial<DirectorShow>): DirectorShow {
  const src = { ...(local ?? {}), ...(profile?.show ?? {}) };
  const voiceId = typeof src.voiceId === 'string' && VOICE_ID.test(src.voiceId) ? src.voiceId : undefined;
  return {
    style: oneOf(DIRECTOR_STYLES, src.style, DEFAULT_SHOW.style),
    energy: oneOf(DIRECTOR_ENERGIES, src.energy, DEFAULT_SHOW.energy),
    length: oneOf(DIRECTOR_LENGTHS, src.length, DEFAULT_SHOW.length),
    focus: oneOf(DIRECTOR_FOCUSES, src.focus, DEFAULT_SHOW.focus),
    spoilers: oneOf(DIRECTOR_SPOILERS, src.spoilers, DEFAULT_SHOW.spoilers),
    ...(voiceId ? { voiceId } : {}),
  };
}

/** The profile's follows decide the focus; "my calls" is remembered locally (no profile field). */
export function focusFromProfile(profile: Pick<DirectorProfile, 'followedTeams' | 'followedPlayers'> | undefined, fanTeam?: string, localFocus?: Focus['kind']): Focus {
  const team = profile?.followedTeams[0] ?? (profile ? undefined : fanTeam);
  if (team) return { kind: 'team', team };
  const player = profile?.followedPlayers[0];
  if (player) return { kind: 'player', name: player };
  return localFocus === 'calls' ? { kind: 'calls' } : localFocus === 'analysis' ? { kind: 'analysis' } : { kind: 'game' };
}

export function draftFrom(o: { profile?: DirectorProfile; fanTeam?: string; local?: Partial<DirectorShow>; localFocus?: Focus['kind']; language?: DirectorLanguage }): ShowDraft {
  const show = showFromProfile(o.profile, o.local);
  return {
    style: show.style, energy: show.energy, length: show.length, spoilers: show.spoilers, ...(show.voiceId ? { voiceId: show.voiceId } : {}),
    language: o.language ?? o.profile?.language ?? 'en',
    familiarity: o.profile?.familiarity ?? 'regular',
    focus: focusFromProfile(o.profile, o.fanTeam, o.localFocus),
  };
}

const LENS: Record<Focus['kind'], DirectorShow['focus']> = { team: 'my-team', player: 'players', game: 'drama', analysis: 'analysis', calls: 'my-calls' };
export const showOfDraft = (d: ShowDraft): DirectorShow => ({
  style: d.style, energy: d.energy, length: d.length, focus: LENS[d.focus.kind], spoilers: d.spoilers ?? DEFAULT_SHOW.spoilers, ...(d.voiceId ? { voiceId: d.voiceId } : {}),
});

/**
 * One PATCH that remembers the whole sheet. Focus rewrites the follows the server already
 * understands: a team or player becomes the lens; turning points / my calls clear the lens so
 * the editor leads with the game's decisive plays (and the fan's own call when one exists).
 */
export function profilePatch(d: ShowDraft): Partial<DirectorProfile> {
  const follows: Pick<DirectorProfile, 'followedTeams' | 'followedPlayers'> =
    d.focus.kind === 'team' && TEAM.test(d.focus.team) ? { followedTeams: [d.focus.team], followedPlayers: [] }
      : d.focus.kind === 'player' && d.focus.name.trim() ? { followedTeams: [], followedPlayers: [d.focus.name.trim().slice(0, 80)] }
        : { followedTeams: [], followedPlayers: [] };
  return { language: d.language, familiarity: d.familiarity, show: showOfDraft(d), ...follows };
}

/** The per-request override (request › profile › defaults on the server) plus moment budget. */
export function requestOf(d: ShowDraft): { language: DirectorLanguage; maxMoments: number; show: DirectorShow } {
  return { language: d.language, maxMoments: LENGTH_MOMENTS[d.length], show: showOfDraft(d) };
}

/** Cache identity for one generated take: anything that can change the script or its lens. */
export function takeKey(d: ShowDraft): string {
  const f = d.focus.kind === 'team' ? `team:${d.focus.team}` : d.focus.kind === 'player' ? `player:${d.focus.name.toLowerCase()}` : d.focus.kind;
  return [d.style, d.energy, d.length, d.familiarity, f, d.spoilers ?? DEFAULT_SHOW.spoilers].join('|');
}

// ── Voice availability ────────────────────────────────────────────────

export type SpeechCapabilities = { speechConfigured?: boolean; voicesAvailable?: boolean | number; voiceCatalog?: 'ok' | 'unavailable' | 'not_configured'; languages?: readonly string[] } | undefined;
export type VoiceGate =
  | { state: 'checking' }
  /** The server cannot speak: no fake voices, captions only. */
  | { state: 'not-connected' }
  /** Speech works but the catalog is unavailable: the server's own default voice. */
  | { state: 'house' }
  | { state: 'ready'; voices: DirectorVoice[] };

export function voiceGate(capabilities: SpeechCapabilities | 'loading', voices: DirectorVoice[] | undefined | 'loading', language: DirectorLanguage): VoiceGate {
  if (capabilities === 'loading') return { state: 'checking' };
  if (capabilities?.speechConfigured && capabilities.voiceCatalog === 'unavailable') return { state: 'house' };
  if (!capabilities?.speechConfigured || capabilities.voicesAvailable === false || capabilities.voicesAvailable === 0) {
    return capabilities?.speechConfigured && (capabilities.voicesAvailable === false || capabilities.voicesAvailable === 0) ? { state: 'house' } : { state: 'not-connected' };
  }
  if (voices === 'loading') return { state: 'checking' };
  const usable = (voices ?? []).filter((v) => v && VOICE_ID.test(v.voiceId) && !!v.name && (!v.languages?.length || v.languages.includes(language)));
  return usable.length ? { state: 'ready', voices: usable } : { state: 'house' };
}

/** A saved voice that the catalog no longer lists is dropped rather than silently replaced. */
export function selectedVoice(gate: VoiceGate, voiceId?: string): DirectorVoice | undefined {
  return gate.state === 'ready' ? gate.voices.find((v) => v.voiceId === voiceId) : undefined;
}

/** Whether the script itself is model-written for the chosen style, or factual templates only. */
export function writerConnected(c: { scriptMode?: 'model' | 'template'; narratorConfigured?: boolean } | undefined): boolean | undefined {
  if (!c) return undefined;
  if (c.scriptMode) return c.scriptMode === 'model';
  return c.narratorConfigured;
}

// ── Single voice preview ──────────────────────────────────────────────

export type AudioLike = { src: string; currentTime: number; play(): Promise<void>; pause(): void; onended: (() => void) | null; onerror: (() => void) | null };

/** One preview at a time: tapping another voice stops the first; tapping the same one stops it. */
export class VoicePreview {
  current?: string;
  private listeners = new Set<(id?: string) => void>();
  constructor(private readonly audio: AudioLike) {
    audio.onended = () => this.set(undefined);
    audio.onerror = () => this.set(undefined);
  }
  subscribe(fn: (id?: string) => void) { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; }
  private set(id?: string) { this.current = id; for (const fn of this.listeners) fn(id); }
  toggle(voiceId: string, url: string | undefined): string | undefined {
    if (this.current === voiceId || !url) { this.stop(); return undefined; }
    this.audio.pause();
    this.audio.src = url;
    this.audio.currentTime = 0;
    this.set(voiceId);
    void this.audio.play().catch(() => { if (this.current === voiceId) this.set(undefined); });
    return voiceId;
  }
  stop() {
    this.audio.pause();
    if (this.current !== undefined) this.set(undefined);
  }
}

// ── Word timings → karaoke captions ───────────────────────────────────

export type Word = { text: string; startMs: number; endMs: number };

/** The word being spoken at `ms`: the last word that has started (it stays lit through a pause). */
export function activeWordIndex(words: readonly Word[], ms: number): number {
  let lo = 0, hi = words.length - 1, found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (words[mid]!.startMs <= ms) { found = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return found;
}

/** Caption pages over the timed words: break after a sentence end, or at `max` words. */
export function wordPages(words: readonly Word[], max = 9, maxCharacters = 68): [number, number][] {
  const pages: [number, number][] = [];
  let start = 0;
  let characters = 0;
  words.forEach((w, i) => {
    if (i > start && characters + w.text.length + 1 > maxCharacters) {
      pages.push([start, i]); start = i; characters = 0;
    }
    characters += w.text.length + (i > start ? 1 : 0);
    const end = /[.!?।。！？]["”’)]*$/u.test(w.text) || i - start + 1 >= max;
    if (end || i === words.length - 1) { pages.push([start, i + 1]); start = i + 1; characters = 0; }
  });
  return pages;
}

export function pageOfWord(pages: readonly [number, number][], index: number): number {
  const i = pages.findIndex(([a, b]) => index >= a && index < b);
  return i < 0 ? 0 : i;
}

/** Usable timings only: finite, ordered, non-empty. Otherwise the player keeps sentence captions. */
export function validWords(words: readonly Word[] | undefined): Word[] | undefined {
  if (!words?.length) return undefined;
  const ok = words.every((w, i) => w.text.trim() && Number.isFinite(w.startMs) && Number.isFinite(w.endMs) && w.endMs >= w.startMs && (i === 0 || w.startMs >= words[i - 1]!.startMs));
  return ok ? [...words] : undefined;
}

// ── Scene beats ───────────────────────────────────────────────────────

export type Beat = 'score' | 'player' | 'swing';
const norm = (s: string) => s.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

/** A spoken word that names the moment's score, its player, or a lead swing fires a visual beat. */
export function beatOf(word: string | undefined, m?: { homeScore: number; awayScore: number; player?: { name: string }; kind: string }): Beat | undefined {
  if (!word || !m) return undefined;
  const w = norm(word);
  if (!w) return undefined;
  if (w === String(m.homeScore) || w === String(m.awayScore)) return 'score';
  if (m.player && m.player.name.split(/\s+/).map(norm).filter((p) => p.length > 2).includes(w)) return 'player';
  if ((m.kind === 'lead_change' || m.kind === 'comeback') && /^(lead|leads|ahead|front|tie|tied|back|comeback|swing)$/.test(w)) return 'swing';
  return undefined;
}

/** The single decisive moment that earns the cinematic treatment: highest rank, latest on ties. */
export function decisiveMomentId(d: Pick<DirectorResponse, 'moments'>): string | undefined {
  let best: DirectorResponse['moments'][number] | undefined;
  for (const m of d.moments) if (!best || m.rankScore > best.rankScore || (m.rankScore === best.rankScore && m.seq > best.seq)) best = m;
  return best?.momentId;
}

// ── Copy ──────────────────────────────────────────────────────────────

type L = { en: string; hi: string; zh: string };
export const STYLE_COPY: Record<DirectorStyle, { name: L; tag: L; sample: string }> = {
  play_by_play: { name: { en: 'Play-by-play', hi: 'प्ले-बाय-प्ले', zh: '解说' }, tag: { en: 'Every possession, called live', hi: 'हर पज़ेशन, लाइव अंदाज़ में', zh: '每个回合，现场解说' }, sample: '“Pull-up from the wing… GOT IT. Lead’s back to four!”' },
  analyst: { name: { en: 'Analyst', hi: 'विश्लेषक', zh: '分析师' }, tag: { en: 'Why it worked, in plain words', hi: 'क्यों हुआ, आसान शब्दों में', zh: '讲清楚为什么' }, sample: '“Watch the switch — it leaves the corner wide open.”' },
  storyteller: { name: { en: 'Storyteller', hi: 'कहानीकार', zh: '讲故事' }, tag: { en: 'The game as a short film', hi: 'मैच एक छोटी फ़िल्म की तरह', zh: '把比赛讲成短片' }, sample: '“One quarter left to change the whole night.”' },
  explain: { name: { en: 'Explain like I’m new', hi: 'आसान भाषा में', zh: '新手讲解' }, tag: { en: 'Who’s who and why it matters', hi: 'कौन कौन है और क्यों अहम', zh: '谁是谁，为什么重要' }, sample: '“That’s a three-pointer — from behind the arc, it counts for three.”' },
  fan: { name: { en: 'Superfan', hi: 'सुपरफ़ैन', zh: '铁杆球迷' }, tag: { en: 'Your side, no neutrality', hi: 'पूरी तरह आपकी टीम के साथ', zh: '只站你这边' }, sample: '“Are you KIDDING me?! That’s OUR guy!”' },
};
export const ENERGY_COPY: Record<DirectorEnergy, L> = {
  calm: { en: 'Calm', hi: 'शांत', zh: '沉稳' }, balanced: { en: 'Balanced', hi: 'संतुलित', zh: '适中' }, electric: { en: 'Electric', hi: 'जोशीला', zh: '激情' },
};
export const LENGTH_COPY: Record<DirectorLength, L> = {
  short: { en: '30 sec', hi: '30 सेकंड', zh: '30 秒' }, standard: { en: '60 sec', hi: '60 सेकंड', zh: '60 秒' }, extended: { en: '120 sec', hi: '120 सेकंड', zh: '120 秒' },
};
export const FAMILIARITY_COPY: Record<Familiarity, L> = {
  new: { en: 'New', hi: 'नए', zh: '新手' }, regular: { en: 'Regular', hi: 'नियमित', zh: '常看' }, expert: { en: 'Expert', hi: 'विशेषज्ञ', zh: '专家' },
};
